/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React, { useState } from 'react';
import { Activity, AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { riskMeta } from '@/lib/careAi';

const EMPTY_SCALE = { date: '', news2: '', braden: '', barthel: '' };

const toInt = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));

/** Form compatto per inserire punteggi NEWS2 / Braden / Barthel (opzionali). */
export const ScalesEditor = ({ scales, onChange }) => {
  const update = (i, k, v) => onChange(scales.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2" data-testid="care-ai-scales-editor">
      <Label className="text-xs uppercase text-muted-foreground">Scale cliniche (opzionali)</Label>
      {scales.map((s, i) => (
        <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-2 items-center">
          <Input type="date" value={s.date} onChange={(e) => update(i, 'date', e.target.value)} data-testid={`care-ai-scale-date-${i}`} />
          <Input type="number" min={0} max={20} placeholder="NEWS2" value={s.news2} onChange={(e) => update(i, 'news2', e.target.value)} data-testid={`care-ai-scale-news2-${i}`} />
          <Input type="number" min={6} max={23} placeholder="Braden" value={s.braden} onChange={(e) => update(i, 'braden', e.target.value)} data-testid={`care-ai-scale-braden-${i}`} />
          <Input type="number" min={0} max={100} placeholder="Barthel" value={s.barthel} onChange={(e) => update(i, 'barthel', e.target.value)} data-testid={`care-ai-scale-barthel-${i}`} />
          <Button variant="ghost" size="icon" aria-label="Rimuovi riga" onClick={() => onChange(scales.filter((_, idx) => idx !== i))}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...scales, { ...EMPTY_SCALE }])} data-testid="care-ai-scale-add">
        <Plus className="h-3.5 w-3.5" /> Aggiungi rilevazione
      </Button>
    </div>
  );
};

export const toScalePayload = (scales) =>
  scales
    .filter((s) => s.date)
    .map((s) => ({ date: s.date, news2: toInt(s.news2), braden: toInt(s.braden), barthel: toInt(s.barthel) }));

/** Pulsante + opzioni per il calcolo del punteggio di declino. */
export const DeclineScoreForm = ({ busy, onRun }) => {
  const [days, setDays] = useState(14);
  const [scales, setScales] = useState([]);
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="w-28">
          <Label className="text-xs uppercase text-muted-foreground">Finestra (giorni)</Label>
          <Input type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value) || 14)} data-testid="care-ai-decline-days" />
        </div>
        <Button onClick={() => onRun({ days, scales: toScalePayload(scales) })} disabled={busy} data-testid="care-ai-decline-run">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />} Calcola rischio di declino
        </Button>
      </div>
      <ScalesEditor scales={scales} onChange={setScales} />
      <p className="text-[11px] text-muted-foreground">
        Il modello incrocia parametri vitali OSS, note del team e scale inserite. Supporto decisionale: non sostituisce la valutazione clinica.
      </p>
    </div>
  );
};

/** Esito del punteggio di declino. */
export const DeclineScoreResult = ({ result, createdAt }) => {
  if (!result) return null;
  const meta = riskMeta(result.livello_rischio);
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-3" data-testid="care-ai-decline-result">
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`h-12 w-12 rounded-xl border flex items-center justify-center font-display text-2xl font-bold ${meta.cls}`} data-testid="care-ai-risk-level">
          {result.livello_rischio ?? '—'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Rischio {meta.label.toLowerCase()} <span className="text-muted-foreground font-normal">(scala 1–5)</span></div>
          <div className="text-[11px] text-muted-foreground">
            {createdAt ? new Date(createdAt).toLocaleString('it') : ''}
            {result.dati_usati ? ` · ${result.dati_usati.vitali} vitali · ${result.dati_usati.note} note · ${result.dati_usati.scale} scale` : ''}
          </div>
        </div>
        {(result.parse_error || result.partial) && (
          <Badge variant="outline" className="text-[10px] gap-1"><AlertTriangle className="h-3 w-3" /> Risposta {result.parse_error ? 'non strutturata' : 'parziale'}</Badge>
        )}
      </div>
      {result.analisi_trend && <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="care-ai-trend">{result.analisi_trend}</p>}
      {result.raccomandazione && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 text-sm" data-testid="care-ai-recommendation">
          <div className="text-[11px] uppercase font-semibold text-primary mb-1">Raccomandazione per l'equipe</div>
          <p className="whitespace-pre-wrap">{result.raccomandazione}</p>
        </div>
      )}
      {result.indicatori_critici?.length > 0 && (
        <ul className="space-y-1" data-testid="care-ai-indicators">
          {result.indicatori_critici.map((x, i) => (
            <li key={i} className="text-xs flex items-start gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /> {x}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
